import { defineStore } from 'pinia'

/**
 * Global snackbar store.
 *
 * Usage from any component or store:
 *   const snackbar = useSnackbarStore()
 *   snackbar.show('Logged in successfully')
 *   snackbar.show('Something went wrong', 'error')
 *   snackbar.show('Item saved', 'success', 5000)
 */
export const useSnackbarStore = defineStore('snackbar', {
  state: () => ({
    visible: false,
    message: '',
    /** 'success' | 'error' | 'info' | 'warning' */
    type: 'info',
    durationMs: 3000,
    _timerId: null
  }),

  actions: {
    /**
     * Show a snackbar notification.
     * @param {string} message  - Text to display
     * @param {'success'|'error'|'info'|'warning'} [type='info'] - Visual variant
     * @param {number} [durationMs=3000] - Auto-dismiss delay (0 = persistent)
     */
    show(message, type = 'info', durationMs = 3000) {
      // Clear any pending auto-dismiss
      if (this._timerId) {
        clearTimeout(this._timerId)
        this._timerId = null
      }

      this.message = message
      this.type = type
      this.durationMs = durationMs
      this.visible = true

      if (durationMs > 0) {
        this._timerId = setTimeout(() => {
          this.dismiss()
        }, durationMs)
      }
    },

    /** Convenience methods */
    success(message, durationMs = 3000) {
      this.show(message, 'success', durationMs)
    },

    error(message, durationMs = 4000) {
      this.show(message, 'error', durationMs)
    },

    warning(message, durationMs = 3500) {
      this.show(message, 'warning', durationMs)
    },

    info(message, durationMs = 3000) {
      this.show(message, 'info', durationMs)
    },

    dismiss() {
      if (this._timerId) {
        clearTimeout(this._timerId)
        this._timerId = null
      }
      this.visible = false
    }
  }
})
